import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

import { loadOrderBoard, loadOrders, searchOrders } from './api';
import { Empty, Failed, Loading, Pill, SearchField, WorkspaceTabs } from './parts';
import { useLiveResource } from './useLive';
import { tx } from './i18n';
import { codeLabel, orderReferenceLabel } from './labels';

// The orders as a table, the other way to see them beside the calendar: thirty
// days of figures on top, then one line an order, newest first.

const PAGE_SIZE = 50;
const DAY_MS = 24 * 60 * 60 * 1000;
const FIGURE_DAYS = 30;

const PAYMENT_TONE = {
  succeeded: 'ok', processing: 'info', requires_action: 'warn', requires_payment_method: 'warn',
  requires_confirmation: 'warn', requires_capture: 'warn', canceled: 'bad',
};

const DELIVERY_TONE = {
  created: 'flat', label_printed: 'warn', dispatched: 'info', in_transit: 'info',
  delivered: 'ok', returned: 'warn', lost: 'bad', cancelled: 'flat',
};

function money(minor, currency) {
  return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(Number(minor) / 100);
}

function placedAt(value) {
  return new Date(Number(value)).toLocaleString(undefined, {
    month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
  });
}

function customerName(order) {
  return `${order.firstName || ''} ${order.lastName || ''}`.trim() || order.email;
}

function regionName(code) {
  try {
    return new Intl.DisplayNames(undefined, { type: 'region' }).of(code) || code;
  } catch {
    return code;
  }
}

// Sent once a shipment has left; a shipment waiting to leave has its label printed.
function fulfilment(order) {
  if (order.shipmentCount > order.labelCount) return { tone: 'ok', label: tx('ui.orders.table.fulfilled') };
  if (order.labelCount > 0) return { tone: 'warn', label: tx('ui.orders.label_printed_6225c56f') };
  return { tone: 'bad', label: tx('ui.orders.table.unfulfilled') };
}

function paymentCell(order) {
  if (order.paymentStatus === null || order.paymentStatus === undefined) return null;
  if (order.refundedMinor > 0) {
    const whole = order.refundedMinor >= order.totalMinor;
    return (
      <Pill tone="warn">
        {whole ? tx('ui.orders.table.refunded') : tx('ui.orders.table.partly_refunded')}
      </Pill>
    );
  }
  return (
    <Pill tone={PAYMENT_TONE[order.paymentStatus] ?? 'flat'}>
      {order.paymentStatus === 'succeeded' ? tx('ui.orders.table.paid') : codeLabel('paymentStatus', order.paymentStatus)}
    </Pill>
  );
}

// One figure's thirty days as a small line.
function Spark({ values }) {
  const highest = Math.max(1, ...values);
  const step = 72 / Math.max(1, values.length - 1);
  const points = values.map((value, index) => `${(index * step).toFixed(1)},${(22 - (value / highest) * 20).toFixed(1)}`);
  return (
    <svg className="xo-orders-spark" viewBox="0 0 72 24" aria-hidden="true">
      <polyline points={points.join(' ')} />
    </svg>
  );
}

function Figure({ label, value, previous, current, days }) {
  const change = previous > 0 ? Math.round(((current - previous) / previous) * 100) : null;
  return (
    <div className="xo-orders-figure">
      <span>{label}</span>
      <strong>{value}</strong>
      {change !== null && (
        <small className={change < 0 ? 'is-down' : 'is-up'}>{change < 0 ? '↓' : '↑'} {Math.abs(change)}%</small>
      )}
      {days && <Spark values={days} />}
    </div>
  );
}

// The day of a moment within the figures' thirty days, oldest first, or null outside them.
function dayIndex(at, start) {
  const index = Math.floor((at - start) / DAY_MS);
  return index >= 0 && index < FIGURE_DAYS ? index : null;
}

function Figures({ scope }) {
  // Sixty days, so each figure can say how the last thirty compare with the thirty before.
  const [range] = useState(() => {
    const to = Date.now() + 1;
    return { from: to - (FIGURE_DAYS * 2 * DAY_MS), to, split: to - (FIGURE_DAYS * DAY_MS) };
  });
  const load = useCallback(() => loadOrderBoard({ from: range.from, to: range.to, scope }), [range, scope]);
  const board = useLiveResource(['orders', 'shipments'], load);
  const figures = useMemo(() => {
    if (board.data === null) return null;
    const placedByUuid = new Map(board.data.placed.map((order) => [order.uuid, order]));
    const series = () => Array.from({ length: FIGURE_DAYS }, () => 0);
    const blank = () => ({ now: 0, before: 0, days: series() });
    const orders = blank();
    const items = blank();
    const refunds = blank();
    const sent = blank();
    const delivered = blank();
    const count = (figure, at, amount = 1) => {
      if (at >= range.split) {
        figure.now += amount;
        const index = dayIndex(at, range.split);
        if (index !== null) figure.days[index] += amount;
      } else figure.before += amount;
    };
    board.data.placed.forEach((order) => {
      count(orders, order.placedAt);
      count(items, order.placedAt, order.unitCount);
      if (order.refundedMinor > 0) count(refunds, order.placedAt);
    });
    let waited = 0;
    let waitedCount = 0;
    let waitedBefore = 0;
    let waitedBeforeCount = 0;
    board.data.dispatched.forEach((shipment) => {
      count(sent, shipment.dispatchedAt);
      if (shipment.deliveredAt !== null) count(delivered, shipment.deliveredAt);
      const order = placedByUuid.get(shipment.orderUuid);
      if (!order) return;
      if (shipment.dispatchedAt >= range.split) {
        waited += shipment.dispatchedAt - order.placedAt;
        waitedCount += 1;
      } else {
        waitedBefore += shipment.dispatchedAt - order.placedAt;
        waitedBeforeCount += 1;
      }
    });
    const hours = waitedCount > 0 ? waited / waitedCount / 3600000 : null;
    const hoursBefore = waitedBeforeCount > 0 ? waitedBefore / waitedBeforeCount / 3600000 : 0;
    return { orders, items, refunds, sent, delivered, hours, hoursBefore };
  }, [board.data, range]);

  if (figures === null) return null;
  const { orders, items, refunds, sent, delivered, hours, hoursBefore } = figures;
  return (
    <section className="xo-orders-figures" aria-label={tx('ui.orders.table.last_30_days')}>
      <div className="xo-orders-figure is-range"><strong>{tx('ui.orders.table.last_30_days')}</strong></div>
      <Figure label={tx('ui.orders.table.orders')} value={orders.now} current={orders.now} previous={orders.before} days={orders.days} />
      <Figure label={tx('ui.orders.table.items_ordered')} value={items.now} current={items.now} previous={items.before} days={items.days} />
      <Figure label={tx('ui.orders.table.refunds')} value={refunds.now} current={refunds.now} previous={refunds.before} days={refunds.days} />
      <Figure label={tx('ui.orders.table.orders_fulfilled')} value={sent.now} current={sent.now} previous={sent.before} days={sent.days} />
      <Figure label={tx('ui.orders.table.orders_delivered')} value={delivered.now} current={delivered.now} previous={delivered.before} days={delivered.days} />
      <Figure label={tx('ui.orders.table.time_to_fulfil')}
        value={hours === null ? '—' : tx('ui.orders.table.hours', { value: hours.toFixed(1) })}
        current={hours ?? 0} previous={hours === null ? 0 : hoursBefore} />
    </section>
  );
}

export default function OrdersTable({ scope, onOpen, viewSwitch }) {
  const [status, setStatus] = useState('all');
  const [query, setQuery] = useState('');
  const [debounced, setDebounced] = useState('');
  const [page, setPage] = useState(0);

  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(query.trim()), 250);
    return () => window.clearTimeout(timer);
  }, [query]);
  useEffect(() => { setPage(0); }, [debounced, scope, status]);

  const searching = debounced.length >= 2;
  const load = useCallback(() => (searching
    ? searchOrders({ query: debounced, status, limit: PAGE_SIZE, scope })
      .then(({ orders, hasMore }) => ({ orders, total: orders.length, hasMore }))
    : loadOrders({ limit: PAGE_SIZE, offset: page * PAGE_SIZE, scope, status })
  ), [debounced, page, scope, searching, status]);
  const list = useLiveResource(['orders', 'shipments'], load);
  const orders = list.data?.orders ?? [];
  const total = list.data?.total ?? 0;
  const first = total === 0 ? 0 : (page * PAGE_SIZE) + 1;
  const last = Math.min(total, (page + 1) * PAGE_SIZE);

  return (
    <div className="xo-orders-table-view">
      <Figures scope={scope} />
      <section className="xo-orders-table-card">
        <header className="xo-orders-table-bar">
          <WorkspaceTabs className="is-compact" value={status} label={tx('ui.orders.order_status_22edd6c1')}
            onChange={setStatus}
            tabs={[
              { value: 'all', label: tx('ui.orders.table.all') },
              { value: 'unsent', label: tx('ui.orders.unsent_0d09fa0d') },
              { value: 'sent', label: tx('ui.orders.sent_35f49dcf') },
            ]} />
          <div className="xo-orders-table-tools">
            <SearchField value={query} onChange={setQuery} placeholder={tx('ui.orders.search_orders_7ab30154')} />
            {viewSwitch}
          </div>
        </header>
        {list.error && list.data === null ? <Failed message={list.error} onRetry={list.refresh} />
          : list.data === null ? <Loading label={tx('ui.orders.reading_the_order_calendar_7afcd827')} />
            : orders.length === 0 ? <Empty title={tx('ui.orders.no_orders_in_this_period_f029b9fb')} />
              : (
                <div className="xo-table-wrap xo-orders-table-scroll">
                  <table className="xo-table xo-orders-table">
                    <thead>
                      <tr>
                        <th>{tx('ui.orders.order_1d75774c')}</th>
                        <th>{tx('ui.orders.table.date')}</th>
                        <th>{tx('ui.orders.table.customer')}</th>
                        <th>{tx('ui.orders.table.ship_to')}</th>
                        <th>{tx('ui.orders.table.office')}</th>
                        <th className="xo-num">{tx('ui.orders.table.total')}</th>
                        <th>{tx('ui.orders.table.payment')}</th>
                        <th>{tx('ui.orders.table.fulfilment')}</th>
                        <th className="xo-num">{tx('ui.orders.table.items')}</th>
                        <th>{tx('ui.orders.table.delivery')}</th>
                        <th>{tx('ui.orders.table.carrier')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {orders.map((order) => {
                        const step = fulfilment(order);
                        const shipment = order.latestShipment;
                        return (
                          <tr key={order.uuid} className="xo-click" tabIndex={0} onClick={() => onOpen(order.uuid)}
                            onKeyDown={(event) => { if (event.key === 'Enter') onOpen(order.uuid); }}>
                            <td className="xo-mono">{orderReferenceLabel(order.trackingCode)}</td>
                            <td>{placedAt(order.placedAt)}</td>
                            <td><strong>{customerName(order)}</strong></td>
                            <td>{order.address.city}, {regionName(order.address.countryCode)}</td>
                            <td>{order.responsibleLocation?.code ?? '—'}</td>
                            <td className="xo-num">{money(order.totalMinor, order.currency)}</td>
                            <td>{paymentCell(order)}</td>
                            <td><Pill tone={step.tone}>{step.label}</Pill></td>
                            <td className="xo-num">{order.unitCount}</td>
                            <td>{shipment ? <Pill tone={DELIVERY_TONE[shipment.status] ?? 'flat'}>{codeLabel('shipmentStatus', shipment.status)}</Pill> : '—'}</td>
                            <td>{shipment?.carrierName ?? '—'}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
        {!searching && total > 0 && (
          <footer className="xo-orders-table-foot">
            <span>{tx('ui.orders.table.range', { first, last, total })}</span>
            <div>
              <button type="button" className="xo-btn-icon" disabled={page === 0} onClick={() => setPage(page - 1)}
                aria-label={tx('ui.collections.previous')}>
                <ChevronLeft aria-hidden="true" />
              </button>
              <button type="button" className="xo-btn-icon" disabled={last >= total} onClick={() => setPage(page + 1)}
                aria-label={tx('ui.collections.next')}>
                <ChevronRight aria-hidden="true" />
              </button>
            </div>
          </footer>
        )}
      </section>
    </div>
  );
}
